import { FC } from 'react';
import {
  Group,
  Text,
  Stack,
  Box,
  Button,
  Skeleton,
  Tooltip,
} from '@mantine/core';
import {
  IconFileCheck,
  IconFileX,
  IconFileMinus,
  IconFlask,
  IconFlaskOff,
  IconCash,
  IconCashOff,
} from '@tabler/icons-react';
import { MedplumClient } from '@medplum/core';
import { Consent, Patient, ServiceRequest, Specimen } from '@medplum/fhirtypes';
import { OrderUtils } from '../utils/order-table.utils';
import { formatDate } from '../utils/common-helpers';
import { useKanbanColumn, KanbanSharedParams } from '../hooks/useKanbanColumn';
import { KANBAN_COLUMNS, KanbanColumn } from '../constants/order.consts';
import { getSubStateLabel } from '../utils/order-substates-helper';
import classes from './OrdersKanbanBoard.module.css';
import { getConsentStatusFromResource } from '../utils/order-utils';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Order card — GitHub-style: white card, thin border, compact
// ---------------------------------------------------------------------------
interface OrderKanbanCardProps {
  order: ServiceRequest;
  patients: Map<string, Patient>;
  specimens: Map<string, Specimen[]>;
  consents: Map<string, Consent>;
  deposits: Map<string, boolean>;
  onClick: (order: ServiceRequest) => void;
}

const OrderKanbanCard: FC<OrderKanbanCardProps> = ({
  order,
  patients,
  specimens,
  consents,
  deposits,
  onClick,
}) => {
  const { t } = useTranslation(['lims', 'common']);
  const consent = order.id ? consents.get(order.id) : undefined;
  const consentStatus = getConsentStatusFromResource(consent);
  const hasDeposit = order.id ? (deposits.get(order.id) ?? false) : false;
  const orderSpecimens = order.id ? (specimens.get(order.id) ?? []) : [];
  const specimenLinked = orderSpecimens.length > 0;

  return (
    <Box className={classes.card} onClick={() => onClick(order)}>
      <Stack gap={8}>
        {/* Title row */}
        <Text size="sm" fw={600} lineClamp={2} lh={1.3}>
          {OrderUtils.getOrderId(order)}
        </Text>

        {/* Patient */}
        <Text size="xs" c="dimmed" truncate>
          {OrderUtils.getPatientName(
            order,
            patients,
            t('orderList.unknown', 'Unknown')
          )}
        </Text>

        {/* Test type */}
        <Text size="xs" c="dimmed" truncate>
          {OrderUtils.getTestName(
            order,
            t('orderList.unknownTest', 'Unknown Test')
          )}
        </Text>

        {/* Footer */}
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text size="xs" c="dimmed" className={classes.metaText}>
            {formatDate(order.authoredOn)}
          </Text>

          <Group gap={6} wrap="nowrap" align="center">
            {/* Consent */}
            <Tooltip
              label={t(
                `consent.display.${consentStatus === 'not-answered' ? 'notAnswered' : consentStatus === 'permit' ? 'accepted' : 'declined'}`,
                { ns: 'lims' }
              )}
              withArrow
              position="top"
            >
              {consentStatus === 'permit' ? (
                <IconFileCheck size={15} color="var(--mantine-color-green-6)" />
              ) : consentStatus === 'deny' ? (
                <IconFileX size={15} color="var(--mantine-color-red-6)" />
              ) : (
                <IconFileMinus size={15} color="var(--mantine-color-gray-4)" />
              )}
            </Tooltip>

            {/* Specimen */}
            <Tooltip
              label={
                specimenLinked
                  ? t('kanban.specimenLinked', {
                      count: orderSpecimens.length,
                      ns: 'lims',
                      defaultValue: `${orderSpecimens.length} specimen(s)`,
                    })
                  : t('kanban.noSpecimen', {
                      ns: 'lims',
                      defaultValue: 'No specimen',
                    })
              }
              withArrow
              position="top"
            >
              {specimenLinked ? (
                <IconFlask size={15} color="var(--mantine-color-green-6)" />
              ) : (
                <IconFlaskOff size={15} color="var(--mantine-color-gray-4)" />
              )}
            </Tooltip>

            {/* Deposit */}
            <Tooltip
              label={
                hasDeposit
                  ? t('kanban.depositMade', {
                      ns: 'lims',
                      defaultValue: 'Deposit made',
                    })
                  : t('kanban.noDeposit', {
                      ns: 'lims',
                      defaultValue: 'No deposit',
                    })
              }
              withArrow
              position="top"
            >
              {hasDeposit ? (
                <IconCash size={15} color="var(--mantine-color-green-6)" />
              ) : (
                <IconCashOff size={15} color="var(--mantine-color-gray-4)" />
              )}
            </Tooltip>
          </Group>
        </Group>
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Single column — GitHub-style: borderless lane, subtle bg, sticky header
// ---------------------------------------------------------------------------
interface KanbanColumnViewProps {
  col: KanbanColumn;
  medplum: MedplumClient;
  sharedParams: KanbanSharedParams;
  onOrderClick: (order: ServiceRequest) => void;
}

const KanbanColumnView: FC<KanbanColumnViewProps> = ({
  col,
  medplum,
  sharedParams,
  onOrderClick,
}) => {
  const {
    orders,
    patients,
    specimens,
    consents,
    deposits,
    loading,
    loadingMore,
    hasMore,
    totalCount,
    loadMore,
  } = useKanbanColumn(medplum, col.id, sharedParams);
  const { t } = useTranslation(['lims', 'common']);

  const countLabel =
    totalCount !== undefined ? totalCount : loading ? '…' : orders.length;

  return (
    <Box className={classes.column}>
      {/* Column header */}
      <Group
        justify="space-between"
        wrap="nowrap"
        className={classes.columnHeader}
      >
        <Group gap={8} wrap="nowrap">
          <Box
            className={classes.columnColorDot}
            bg={`var(--mantine-color-${col.color}-5)`}
          />
          <Text size="sm" fw={700}>
            {t(getSubStateLabel(col.id), { ns: 'lims' })}
          </Text>
        </Group>
        <Box className={classes.columnCount}>{countLabel}</Box>
      </Group>

      {/* Card list — scrollable */}
      <Box className={classes.columnCardList}>
        <Stack gap={8}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={100} radius={6} />
            ))
          ) : orders.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" py="xl">
              {t('kanban.noItems', { ns: 'lims' })}
            </Text>
          ) : (
            orders.map((order) => (
              <OrderKanbanCard
                key={order.id}
                order={order}
                patients={patients}
                specimens={specimens}
                consents={consents}
                deposits={deposits}
                onClick={onOrderClick}
              />
            ))
          )}

          {!loading && hasMore && (
            <Button
              variant="subtle"
              color="gray"
              size="compact-xs"
              fullWidth
              loading={loadingMore}
              onClick={loadMore}
              h={30}
            >
              {loadingMore ? '' : t('kanban.loadMore', { ns: 'lims' })}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Board — GitHub-style: full-bleed horizontal scroll, columns as lanes
// ---------------------------------------------------------------------------
interface OrdersKanbanBoardProps {
  medplum: MedplumClient;
  sharedParams: KanbanSharedParams;
  onOrderClick: (order: ServiceRequest) => void;
}

export const OrdersKanbanBoard: FC<OrdersKanbanBoardProps> = ({
  medplum,
  sharedParams,
  onOrderClick,
}) => {
  return (
    <Box className={classes.board}>
      <Box className={classes.boardInner}>
        {KANBAN_COLUMNS.map((col) => (
          <KanbanColumnView
            key={col.id}
            col={col}
            medplum={medplum}
            sharedParams={sharedParams}
            onOrderClick={onOrderClick}
          />
        ))}
      </Box>
    </Box>
  );
};
